import { calculateStackGeometry, presentWoodStackResult } from "@foresting-tomorrow/forest_tools_inventory";
import { chipsCalculation, parseMeasurementNumber, displayInventoryValue, measurementRowsFromForm } from "./utils/calculations";
import { useEffect, useState } from "react";

// material ui
import {
  Grid,
  Box,
  Typography,
  Divider,
  Stack,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { pdf, PDFDownloadLink } from "@react-pdf/renderer";

// Material Icons
import DownloadIcon from "@mui/icons-material/DownloadRounded";

// project import
import { selectUiBranding } from "store/reducers/branding";
import IconButton from "components/@extended/IconButton";
import Selector from "components/select/selector";
import TextFieldInput from "components/input/textField";
import { useTranslation } from "utils/locales/utilityFunctions";
import { BuyerStatusChangeProps, FirestoreInventoryItemProps } from "types/auth";
import { ContactType } from "components/shareData/shareDialog";
import { RawWoodOptionProps } from "./options";
import AutoCompleteGrouped from "components/input/autoCompleteGrouped";
import WoodStackDeliveryNote from "components/deliveryNotes/woodStackDeliveryNote";
import WoodChipStackDeliveryNote from "components/deliveryNotes/woodChipStackDeliveryNote";
import WoodLogDeliveryNote from "components/deliveryNotes/woodLogDeliveryNote";
import { beautifyNumber, parseLocalizedNumber } from "utils/numberModification";
import dayjs from "dayjs";
import useAuth from "hooks/useAuth";
import useSpeciesData from "hooks/useSpeciesData";
import {
  getDiameterBasedCompensationPercentage,
  getLogLengthPercentage,
  getSpecieGreenMassFromVolume,
  getSpeciesArrayStackSolidVolumePercentage,
  DEFAULT_VOLUME_TO_CHIPS_PILE_CONVERSION_FACTOR,
} from "@foresting-tomorrow/forest_tools_inventory";
import {
  getSellerData,
  getBuyerData,
  getSpeciesArrayText,
  processLogData,
  DiameterRange,
} from "./utils/handling";
import { getPrimaryCurrency } from "components/rawWoodInventoryElements/utils/getPrimaryCurrency";
import { useSelector } from "react-redux";
import { urlToDataUrl } from "utils/fileUtils";
import { getInternalAppType } from "utils/brandingUtils";
import { InventoryDataConfigProps } from "types/appFunctionality";
import useConfig from "hooks/useConfig";
import { reverseGeocodeNearestAddress } from "services/geolocation/getGeolocation";
import { formatDeliveryNoteCertification } from "./utils/deliveryNoteCertification";

interface RawWoodInputHandlingProps {
  title: string;
  variant: FirestoreInventoryItemProps["inventoryType"];
  inventoryDataConfig: InventoryDataConfigProps;
  disableInputs: boolean;
  buyerApprovedDisable: boolean;
  buyerSelectionLocked: boolean;
  companyOptions: ContactType[];
  buyerOptions: ContactType[];
  statusOptions: RawWoodOptionProps[];
  accessibilityOptions: RawWoodOptionProps[];
  buyerApprovalOptions: RawWoodOptionProps[];
  selectedLoggingCompanyString: string;
  selectedForwardingCompanyString: string;
  selectedChippingCompanyString: string;
  selectedLoggingCompany: ContactType[];
  selectedForwardingCompany: ContactType[];
  selectedChippingCompany: ContactType[];
  selectedStatus: string;
  price: string;
  inventoryStatusNote: string;
  accessibility: string[];
  accessibilityNote: string;
  selectedBuyer: ContactType[];
  buyerApprovalRequirements: NonNullable<
    NonNullable<FirestoreInventoryItemProps["buyer"]>["approvalRequirements"]
  >;
  buyerApprovalRequirementsLoaded: boolean;
  releasedToBuyer: boolean;
  buyerNote: string;
  selectedBuyerApprovalStatus: string;
  location: { lat: number; lng: number };
  uniqueInventoryId: number;
  inventoryDisplayReference: string;
  certification: string[];
  certificationNumbers: NonNullable<FirestoreInventoryItemProps["certificationNumbers"]>;
  certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>;
  certificationNumber: string;
  measurementData: any;
  setSelectedLoggingCompanyString: (value: string) => void;
  setSelectedForwardingCompanyString: (value: string) => void;
  setSelectedChippingCompanyString: (value: string) => void;
  setSelectedLoggingCompany: (value: ContactType[]) => void;
  setSelectedForwardingCompany: (value: ContactType[]) => void;
  setSelectedChippingCompany: (value: ContactType[]) => void;
  setSelectedStatus: (value: string) => void;
  setPrice: (value: string) => void;
  setInventoryStatusNote: (value: string) => void;
  setAccessibility: (value: string[]) => void;
  setAccessibilityNote: (value: string) => void;
  setSelectedBuyer: (value: ContactType[]) => void;
  setReleasedToBuyer: (value: boolean) => Promise<boolean>;
  setDeliveryNoteGenerating: (value: boolean) => void;
  setSelectedBuyerApprovalStatus: (value: BuyerStatusChangeProps["status"]) => void;
  setBuyerNote: (value: string) => void;
  setBuyerApprovalRequirements: (
    requirements: NonNullable<
      NonNullable<FirestoreInventoryItemProps["buyer"]>["approvalRequirements"]
    >,
  ) => void;
  saveFiles: (categories: string[], file: File[]) => Promise<void>;
}

// const filterContacts = createFilterOptions<ContactType>();

const RawWoodInputHandling = ({
  title,
  variant,
  inventoryDataConfig,
  disableInputs,
  buyerApprovedDisable,
  buyerSelectionLocked,
  companyOptions,
  buyerOptions,
  statusOptions,
  accessibilityOptions,
  buyerApprovalOptions,
  selectedBuyer,
  buyerApprovalRequirements,
  buyerApprovalRequirementsLoaded,
  selectedLoggingCompanyString,
  selectedForwardingCompanyString,
  selectedChippingCompanyString,
  selectedLoggingCompany,
  selectedForwardingCompany,
  selectedChippingCompany,
  selectedStatus,
  price,
  inventoryStatusNote,
  accessibility,
  accessibilityNote,
  releasedToBuyer,
  selectedBuyerApprovalStatus,
  buyerNote,
  location,
  uniqueInventoryId,
  inventoryDisplayReference,
  certification,
  certificationNumbers,
  certificationClaims,
  certificationNumber,
  measurementData,
  setSelectedLoggingCompanyString,
  setSelectedForwardingCompanyString,
  setSelectedChippingCompanyString,
  setSelectedLoggingCompany,
  setSelectedForwardingCompany,
  setSelectedChippingCompany,
  setSelectedStatus,
  setPrice,
  setInventoryStatusNote,
  setAccessibility,
  setAccessibilityNote,
  setSelectedBuyer,
  setReleasedToBuyer,
  setSelectedBuyerApprovalStatus,
  setBuyerNote,
  setBuyerApprovalRequirements,
  setDeliveryNoteGenerating,
  saveFiles,
}: RawWoodInputHandlingProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { i18n } = useConfig();
  const { userOrg, user, buyerOrgsById } = useAuth();
  const ui = useSelector(selectUiBranding);
  const { getSpeciesNameAndAbbreviation, getSpeciesType } = useSpeciesData();

  const deliveryNoteCertification = formatDeliveryNoteCertification({
    certification,
    certificationNumbers,
    certificationClaims,
    legacyCertificationNumber: certificationNumber,
  });

  // --- State --- //
  const [printLogoDataUrl, setPrintLogoDataUrl] = useState<string | undefined>(undefined);
  const [nearestAddress, setNearestAddress] = useState<string | null>(null);
  const [generatingDeliveryNote, setGeneratingDeliveryNote] = useState(false);

  useEffect(() => {
    let active = true;
    void reverseGeocodeNearestAddress(location, i18n).then((address) => {
      if (active) setNearestAddress(address);
    });
    return () => {
      active = false;
    };
  }, [location.lat, location.lng, i18n]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = ui.assets?.printHeaderLogo?.url;
      if (!url) return setPrintLogoDataUrl(undefined);
      if (url.endsWith(".png")) {
        // If it's a PNG, we can use it directly
        return setPrintLogoDataUrl(url);
      }
      try {
        const dataUrl = await urlToDataUrl(url);
        if (!cancelled) setPrintLogoDataUrl(dataUrl);
      } catch {
        if (!cancelled) setPrintLogoDataUrl(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ui.assets?.printHeaderLogo?.url]);

  // --- Dynamic values --- //
  const iaVersion = getInternalAppType({
    pathOrHost: window.location.host,
    orgData: userOrg,
    userData: user,
  });

  // --- Handlers --- //
  // const handleContactFilter = (options: any, params: any) => {
  //   const filtered = filterContacts(options, params);
  //   const { inputValue } = params;
  //   // Check for existing option
  //   const isExisting = options.some((option: ContactType) => inputValue === option.email);
  //   // Check input to be email address
  //   const validEmail = validateEmail(inputValue);
  //   if (validEmail && !isExisting) {
  //     filtered.push({
  //       inputValue,
  //       email: inputValue,
  //       label: inputValue,
  //       name: inputValue,
  //       personType: "contact",
  //     });
  //   }
  //   return filtered;
  // };

  const handleChangeBuyer = (e: any, v: any) => {
    if (v.length === 0) {
      setSelectedBuyer([]);
      return;
    }
    const lastItem = v.slice(-1)[0];
    setSelectedBuyer([lastItem]);
  };

  const handleChangeLoggingCompany = (e: any, v: any) => {
    if (v.length === 0) {
      setSelectedLoggingCompany([]);
      setSelectedLoggingCompanyString("");
      return;
    }
    const lastItem = v.slice(-1)[0];
    setSelectedLoggingCompany([lastItem]);
  };

  const handleChangeForwardingCompany = (e: any, v: any) => {
    if (v.length === 0) {
      setSelectedForwardingCompany([]);
      setSelectedForwardingCompanyString("");
      return;
    }
    const lastItem = v.slice(-1)[0];
    setSelectedForwardingCompany([lastItem]);
  };

  const handleChangeChippingCompany = (e: any, v: any) => {
    if (v.length === 0) {
      setSelectedChippingCompany([]);
      setSelectedChippingCompanyString("");
      return;
    }
    const lastItem = v.slice(-1)[0];
    setSelectedChippingCompany([lastItem]);
  };

  const handleDeleviryNoteClick = async (blob: Blob | null) => {
    if (!blob) return;
    // Set fileName for delivery note
    const fileName = `${translate("delivery-note-title")}-${inventoryDisplayReference}.pdf`;
    // Convert blob to file
    const file = new File([blob], fileName, {
      type: "application/pdf",
    });
    // Set categoris
    const categories = ["inventory", "rawWood", variant, "deliveryNote"];
    // Call save files
    await saveFiles(categories, [file]);
  };

  const generateAndSaveDeliveryNote = async () => {
    setGeneratingDeliveryNote(true);
    setDeliveryNoteGenerating(true);
    try {
      await handleDeleviryNoteClick(await pdf(document).toBlob());
    } catch (error) {
      console.error("Unable to generate delivery note while releasing inventory", error);
    } finally {
      setGeneratingDeliveryNote(false);
      setDeliveryNoteGenerating(false);
    }
  };

  // --- Data for creating delivery note PDF --- //
  // Unpack general parameters
  const {
    initSpeciesArray,
    averageLogLength,
    averageLogDiameter,
    averageStackHeight,
    numericMeasurements,
    woodStackResult,
    numberOfSections,
    numberOfSectionsBack,
    measureBothSides,
    averageStackLength,
    stackLengthFront,
    stackLengthBack,
    frontHeights,
    backHeights,
    origins,
    responsible,
    assortment,
    rejectsValue,
    battensValue,
    inputModeBattens,
    inputModeRejects,
    stackingValue,
    stackingOptions,
    straightnessValue,
    straightnessOptions,
    snowIceValue,
    snowIceOptions,
    residuesValue,
    residuesOptions,
    processingValue,
    processingOptions,
    woodChipManualVolume,
    woodChipManualCompFactor,
    woodChipSpecies,
    woodLogAssortment,
    initLogRows,
  } = measurementData;
  // Set seller data
  let seller = getSellerData(userOrg || null, translate);
  // Set buyer data
  const selectedBuyerOrgData = selectedBuyer[0]?.orgId
    ? buyerOrgsById?.[selectedBuyer[0].orgId] || null
    : null;
  let buyer = getBuyerData(selectedBuyerOrgData || null, translate);
  // Set measurement responsible
  let measResponsible = "-";
  if (responsible && responsible.length > 0) {
    measResponsible = responsible[0].name || "-";
  }
  // Set origins
  let originsStr = "-";
  let forestName = "-";
  if (origins && origins.length > 0) {
    const { estateName, activityName, mapName } = origins[0];
    originsStr = `${estateName}, ${activityName}`;
    forestName = mapName || "-";
  }
  // Set assortment
  let assortmentStr = "-";
  if (assortment) {
    assortmentStr = translate(`raw-wood-assortment-${assortment}`);
  } else if (woodLogAssortment) {
    assortmentStr = translate(`raw-wood-assortment-${woodLogAssortment}`);
  }

  let speciesArrText = getSpeciesArrayText(
    variant,
    initSpeciesArray,
    woodChipSpecies,
    initLogRows,
    getSpeciesNameAndAbbreviation,
    translate,
  );

  // Set measurement data
  const logLengthStr = averageLogLength ? `${averageLogLength} m` : "-";
  const diameterStr = averageLogDiameter ? `${averageLogDiameter} cm` : "-";
  const stackLengthStr = averageStackLength ? `${averageStackLength} m` : "-";
  const stackHeightStr = averageStackHeight ? `${averageStackHeight} m` : "-";

  // Get measurement front and back values
  const measArr: { section: number | string; front: string; back: string | undefined }[] = measurementRowsFromForm({
    frontHeights: frontHeights ?? [], backHeights: backHeights ?? [],
    numberOfSections: numberOfSections ?? "", numberOfSectionsBack: numberOfSectionsBack ?? "", measureBothSides: !!measureBothSides,
  }).map(row => ({
    section: row.section,
    front: row.frontHeightM === null ? "-" : `${beautifyNumber(displayInventoryValue(row.frontHeightM, "heightM"), 2)} m`,
    back: !measureBothSides ? undefined : row.backHeightM === null ? "-" : `${beautifyNumber(displayInventoryValue(row.backHeightM, "heightM"), 2)} m`,
  }));
  measArr.push({
    section: translate("stack-delivery-note-stack-length"),
    front: stackLengthFront ? `${stackLengthFront} m` : "-",
    back: measureBothSides && stackLengthBack ? `${stackLengthBack} m` : undefined,
  });

  // Set support and rejects values
  const averageLogLengthMNum = parseMeasurementNumber(averageLogLength || "0");
  const averageLogDiameterCmNum = parseMeasurementNumber(averageLogDiameter || "0");
  const geometry = numericMeasurements ? calculateStackGeometry(numericMeasurements) : null;
  const averageStackHeightMNum = geometry?.averageHeightM ?? 0;
  const averageStackLengthNum = geometry?.averageLengthM ?? 0;
  let supportVol = woodStackResult?.totals.volumeOfSupportLogsM3 ?? 0;
  let rejectsVol = woodStackResult?.totals.volumeOfCullLogsM3 ?? 0;

  // Convert species array
  const spcArray: { species: string; mix: number }[] = initSpeciesArray
    ? initSpeciesArray.map((s: any) => {
      const mix = s.mix ? parseFloat(s.mix.replace(",", ".")) : 100;
      return { species: s.id, mix };
    })
    : [];

  // Get stacking parameter values
  const speciesPercentage =
    spcArray.length > 0 ? getSpeciesArrayStackSolidVolumePercentage(spcArray) : undefined;
  const logDiameterPercentage =
    averageLogDiameterCmNum > 0
      ? getDiameterBasedCompensationPercentage(averageLogDiameterCmNum)
      : undefined;

  let stackingPercentage: number | undefined = undefined;
  if (stackingValue) {
    const item = stackingOptions.find((el: any) => el.id === stackingValue);
    if (item) {
      stackingPercentage = item.adjustment;
    }
  }
  let straightnessPercentage: number | undefined = undefined;
  if (straightnessValue) {
    const item = straightnessOptions.find((el: any) => el.id === straightnessValue);
    if (item) {
      straightnessPercentage = item.adjustment;
    }
  }
  let residuesPercentage: number | undefined = undefined;
  if (residuesValue) {
    const item = residuesOptions.find((el: any) => el.id === residuesValue);
    if (item) {
      residuesPercentage = item.adjustment;
    }
  }
  let snowIcePercentage: number | undefined = undefined;
  if (snowIceValue) {
    const item = snowIceOptions.find((el: any) => el.id === snowIceValue);
    if (item) {
      snowIcePercentage = item.adjustment;
    }
  }
  let processingPercentage: number | undefined = undefined;
  if (processingValue) {
    const item = processingOptions.find((el: any) => el.id === processingValue);
    if (item) {
      processingPercentage = item.adjustment;
    }
  }
  const stackHeightPercentage = geometry?.stackHeightPercentage;

  let speciesType: "foliage" | "needle" | "mixed" = "foliage";
  let speciesTypes: string[] = [];
  if (spcArray && spcArray.length > 0) {
    spcArray.forEach((spc) => {
      const type = getSpeciesType(spc.species);
      if (type && !speciesTypes.includes(type)) {
        speciesTypes.push(type);
      }
    });
    if (speciesTypes.length === 1) {
      speciesType = speciesTypes[0] as "foliage" | "needle";
    } else if (speciesTypes.length > 1) {
      speciesType = "mixed";
    }
  }

  const logLengthPercentage = getLogLengthPercentage(averageLogLengthMNum, variant === "WoodStack" && spcArray.length ? (getSpeciesType(spcArray[0].species) || speciesType) as "foliage" | "needle" | "mixed" : speciesType);

  // Get log values
  let shortestLogLength = 0;
  let longestLogLength = 0;
  let totalLogLength = 0;
  let totalLogVolume = 0;
  let averageLogLengthM = 0;
  let averageLogVolume = 0;
  let rows: any[] = [];
  let classifications: string[] = [];
  if (initLogRows && initLogRows.length > 0) {
    for (const item of initLogRows) {
      const logLength = item.length ? parseFloat(item.length.replace(",", ".")) : 0;
      if (logLength > 0) {
        shortestLogLength =
          shortestLogLength === 0 ? logLength : Math.min(shortestLogLength, logLength);
        longestLogLength = Math.max(longestLogLength, logLength);
        totalLogLength += logLength;
        totalLogVolume += item.volume ? parseFloat(item.volume.replace(",", ".")) : 0;
        let qClass = item.qClass ? item.qClass.toUpperCase() : "";
        if (qClass === "SPLUS") qClass = "S+";
        if (qClass && !classifications.includes(qClass)) {
          classifications.push(qClass);
        }
      }
    }
    averageLogLengthM = totalLogLength / initLogRows.length;
    averageLogVolume = totalLogVolume / initLogRows.length;
    // Create log rows
    rows = initLogRows.map((item: any) => {
      const spcAbr = getSpeciesNameAndAbbreviation(item.species).abbreviation;
      let diameter = item.diameter ? parseFloat(item.diameter.replace(",", ".")) : 0;
      if (item.diameter2) {
        diameter =
          (diameter + (item.diameter2 ? parseFloat(item.diameter2.replace(",", ".")) : 0)) / 2;
      }
      let qClass = item.qClass ? item.qClass.toUpperCase() : "-";
      if (qClass === "SPLUS") qClass = "S+";
      return [
        item.uniqueMeasurementId.toString(),
        spcAbr,
        item.length ? `${item.length} m` : "-",
        diameter ? `${beautifyNumber(diameter, 2)} cm` : "-",
        item.volume ? `${item.volume} m3` : "-",
        qClass,
      ];
    });
  }
  // Create summary data
  const diameterRanges: DiameterRange[] = [
    { min: 13, max: 15.9, label: "13 - 15,9 cm" },
    { min: 16, max: 20.9, label: "16 - 20,9 cm" },
    { min: 21, max: 25.9, label: "21 - 25,9 cm" },
    { min: 26, max: 30.9, label: "26 - 30,9 cm" },
    { min: 31, max: Infinity, label: `${translate("delivery-note-summary-above")} 30 cm` },
  ];

  const logsSummaryData = processLogData(initLogRows, diameterRanges);

  // Calculate volumes
  let result = {
    compensationFactorPercentage: 0,
    stackedVolumeM3: 0,
    volumeM3: 0,
    greenMassTon: 0,
  };
  let manualChippedVolumeRM = 0;
  if (variant === "WoodStack" && woodStackResult) {
    result = presentWoodStackResult(woodStackResult).totals;
  }
  if (variant === "WoodChipStack") {
    const woodChipManualVolumeNum = woodChipManualVolume ? parseLocalizedNumber(woodChipManualVolume) : 0;
    const woodChipManualCompFactorNum = woodChipManualCompFactor
      ? parseMeasurementNumber(woodChipManualCompFactor)
      : 0;
    if (speciesType && !isNaN(woodChipManualVolumeNum) && woodChipManualVolumeNum > 0) {
      // result.volumeM3 = woodChipManualVolumeNum;
      // result.stackedVolumeM3 = woodChipManualVolumeNum;
      manualChippedVolumeRM = woodChipManualVolumeNum;
      let spcStrArr = ["norway spruce", "beech"];
      if (speciesType === "foliage") spcStrArr = ["beech"];
      if (speciesType === "needle") spcStrArr = ["norway spruce"];
      const weight = getSpecieGreenMassFromVolume({
        species: spcStrArr,
        // Manual chipped volume is loose rm; the weight calculation expects solid m³.
        volumeM3: woodChipManualVolumeNum / DEFAULT_VOLUME_TO_CHIPS_PILE_CONVERSION_FACTOR,
      });
      result.greenMassTon = weight;
    } else if (
      speciesType &&
      averageLogLengthMNum > 0 &&
      averageStackLengthNum > 0 &&
      averageStackHeightMNum > 0 &&
      woodChipManualCompFactorNum > 0
    ) {
      const res = chipsCalculation(numericMeasurements ? {
        species: speciesType,
        measurements: numericMeasurements,
        logAverageLengthM: averageLogLengthMNum,
        correctionPercentage: woodChipManualCompFactorNum,
      } : null);
      if (res) result = {
        compensationFactorPercentage: woodChipManualCompFactorNum,
        stackedVolumeM3: displayInventoryValue(res.totals.stackedVolumeM3, "volumeM3"),
        volumeM3: displayInventoryValue(res.totals.volumeM3, "volumeM3"),
        greenMassTon: displayInventoryValue(res.totals.greenMassTon, "massTon"),
      };
    }
  }

  // --- STAK --- //
  let supportStr = "-";
  let rejectsStr = "-";
  if (inputModeBattens === "count" && battensValue && battensValue !== "0") {
    supportStr = `${beautifyNumber(supportVol, 2)} m3 (${battensValue} ${translate(
      "stack-delivery-note-pcs",
    )})`;
  }
  if (inputModeBattens === "volume" && battensValue) {
    supportStr = `${beautifyNumber(supportVol, 2)} m3`;
  }
  if (inputModeRejects === "count" && rejectsValue && rejectsValue !== "0") {
    rejectsStr = `${beautifyNumber(rejectsVol, 2)} m3 (${rejectsValue} ${translate(
      "stack-delivery-note-pcs",
    )})`;
  }
  if (inputModeRejects === "volume" && rejectsValue) {
    rejectsStr = `${beautifyNumber(rejectsVol, 2)} m3`;
  }

  // Create data object for PDF document
  const dataObjectStack = {
    headerLabels: {
      title: translate("delivery-note-title"),
      date: translate("stack-delivery-note-date"),
      buyer: translate("stack-delivery-note-buyer"),
      seller: translate("stack-delivery-note-seller"),
      measuredBy: translate("stack-delivery-note-measured-by"),
      forest: translate("stack-delivery-note-forest"),
      certificate: translate("stack-delivery-note-certificate"),
      claims: translate("stack-delivery-note-claims"),
      note: translate("stack-delivery-note-note"),
    },
    headerData: {
      date: `${dayjs().format("DD.MM.YYYY")}`,
      seller: seller,
      buyer: buyer,
      measuredBy: measResponsible,
      forest: forestName,
      certificateNumber: deliveryNoteCertification.certificateNumbers,
      claims: deliveryNoteCertification.claims,
      note: inventoryStatusNote || "-",
    },

    overviewLabels: {
      header: translate("stack-delivery-note-stack-data"),
      stackNumber: translate("stack-delivery-note-stack-number"),
      assortment: translate("stack-delivery-note-assortment"),
      species: translate("stack-delivery-note-species"),
      effectiveLength: translate("stack-delivery-note-effective-length"),
      avgDiameter: translate("stack-delivery-note-avg-diameter"),
      volumeRm: translate("stack-delivery-note-volume-rm"),
      volumeM3: translate("stack-delivery-note-volume-m3"),
      weightTon: translate("stack-delivery-note-weight-ton"),
      map: translate("stack-delivery-note-map"),
      gps: translate("stack-delivery-note-gps"),
      nearestAddress: translate("nearest-address"),
      origin: translate("stack-delivery-note-origin"),
      link: {
        label: translate("stack-delivery-note-map-link"),
        href: `https://www.google.com/maps?q=${location.lat},${location.lng}`,
      },
    },
    overviewData: {
      stackNumber: inventoryDisplayReference,
      assortment: assortmentStr,
      species: speciesArrText,
      effectiveLength: logLengthStr,
      avgDiameter: diameterStr,
      avgStackHeight: stackHeightStr,
      avgStackLength: stackLengthStr,
      volumeRm: `${result.stackedVolumeM3 ? beautifyNumber(result.stackedVolumeM3, 2) : "-"
        } ${translate("unit-rm")}`,
      volumeM3: `${result.volumeM3 ? beautifyNumber(result.volumeM3, 2) : "-"} m3`,
      weightTon: `${result.greenMassTon ? beautifyNumber(result.greenMassTon, 2) : "-"} ${translate(
        "unit-ton",
      )}`,
      gpsCoordinates: { lat: location.lat, long: location.lng },
      nearestAddress: nearestAddress || undefined,
      origin: originsStr,
    },

    measurementsLabels: {
      header: translate("stack-delivery-note-measurements-and-observations"),
      section: translate("stack-delivery-note-section"),
      front: translate("stack-delivery-note-front"),
      back: translate("stack-delivery-note-back"),
      correction: translate("stack-delivery-note-correction"),
      middleValues: translate("stack-delivery-note-middle-values"),
      height: translate("stack-delivery-note-stack-height"),
      length: translate("stack-delivery-note-stack-length"),
      solidPercentage: translate("stack-delivery-note-solid-percentage"),
      supportCount: translate("stack-delivery-note-support-count"),
      rejectCount: translate("stack-delivery-note-reject-count"),
    },
    measurementsData: {
      measurements: measArr,
      solidInputLabels: {
        base: translate("input-dialog-raw-wood-input-conversion-factor-base"),
        meanDiameter: translate("input-dialog-raw-wood-input-conversion-factor-mean-diameter"),
        stacking: translate("input-dialog-raw-wood-input-conversion-factor-stacking"),
        straightness: translate("input-dialog-raw-wood-input-conversion-factor-straightness"),
        processing: translate("input-dialog-raw-wood-input-conversion-factor-processing"),
        snowAndIce: translate("input-dialog-raw-wood-input-conversion-factor-snow-ice"),
        residues: translate("input-dialog-raw-wood-input-conversion-factor-residues"),
        stackHeight: translate("input-dialog-raw-wood-input-conversion-factor-stack-height"),
        logLength: translate("input-dialog-raw-wood-input-conversion-factor-log-length"),
      },
      solidInputs: {
        base: speciesPercentage !== undefined ? `${beautifyNumber(speciesPercentage, 0)} %` : "-",
        meanDiameter:
          logDiameterPercentage !== undefined
            ? `${beautifyNumber(logDiameterPercentage, 0)} %`
            : "-",
        stacking:
          stackingPercentage !== undefined ? `${beautifyNumber(stackingPercentage, 0)} %` : "-",
        straightness:
          straightnessPercentage !== undefined
            ? `${beautifyNumber(straightnessPercentage, 0)} %`
            : "-",
        processing:
          processingPercentage !== undefined ? `${beautifyNumber(processingPercentage, 0)} %` : "-",
        snowAndIce:
          snowIcePercentage !== undefined ? `${beautifyNumber(snowIcePercentage, 0)} %` : "-",
        residues:
          residuesPercentage !== undefined ? `${beautifyNumber(residuesPercentage, 0)} %` : "-",
        stackHeight:
          stackHeightPercentage !== undefined
            ? `${beautifyNumber(stackHeightPercentage, 0)} %`
            : "-",
        logLength:
          logLengthPercentage !== undefined ? `${beautifyNumber(logLengthPercentage, 0)} %` : "-",
      },
      avgHeight: averageStackHeightMNum ? `${beautifyNumber(displayInventoryValue(averageStackHeightMNum, "heightM"), 2)} m` : "-",
      avgLength: stackLengthStr,
      solidPercentage: `${beautifyNumber(result.compensationFactorPercentage, 2)} %`,
      countSupports: supportStr,
      countRejects: rejectsStr,
    },

    metricsLabels: {
      volumeAndWeight: translate("stack-delivery-note-volume-and-weight"),
      volumeRm: translate("stack-delivery-note-volume-rm"),
      volumeM3: translate("stack-delivery-note-volume-m3"),
      weightTon: translate("stack-delivery-note-weight-ton"),
      bark: false
        ? translate("stack-delivery-note-under-bark")
        : translate("stack-delivery-note-over-bark"),
    },
    metricsData: {
      volumeRm: `${result.stackedVolumeM3 ? beautifyNumber(result.stackedVolumeM3, 2) : "-"
        } ${translate("unit-rm")}`,
      volumeM3: `${result.volumeM3 ? beautifyNumber(result.volumeM3, 2) : "-"} m3`,
      weightTon: `${result.greenMassTon ? beautifyNumber(result.greenMassTon, 2) : "-"} ${translate(
        "unit-ton",
      )}`,
    },

    calculationNotice: translate("input-dialog-raw-wood-input-conversion-factor-rounding-notice"),
    stackDeliveryNoteFooter: translate(`print-footer-${iaVersion}`),
  };

  // --- FLIS --- //
  const dataObjectChipStack = {
    headerLabels: {
      title: translate("delivery-note-title"),
      date: translate("stack-delivery-note-date"),
      buyer: translate("stack-delivery-note-buyer"),
      seller: translate("stack-delivery-note-seller"),
      measuredBy: translate("stack-delivery-note-measured-by"),
      forest: translate("stack-delivery-note-forest"),
      certificate: translate("stack-delivery-note-certificate"),
      claims: translate("stack-delivery-note-claims"),
      note: translate("stack-delivery-note-note"),
    },
    headerData: {
      date: `${dayjs().format("DD.MM.YYYY")}`,
      seller: seller,
      buyer: buyer,
      measuredBy: measResponsible,
      certificateNumber: deliveryNoteCertification.certificateNumbers,
      claims: deliveryNoteCertification.claims,
      note: inventoryStatusNote || "-",
      forest: forestName,
    },

    overviewLabels: {
      header: translate("stack-delivery-note-stack-data"),
      stackNumber: translate("stack-delivery-note-stack-number"),
      assortment: translate("stack-delivery-note-assortment"),
      species: translate("stack-delivery-note-species"),
      depth: translate("chip-stack-delivery-note-depth"),
      volumeRm: translate("stack-delivery-note-volume-rm"),
      volumeM3: translate("stack-delivery-note-volume-m3"),
      volumeProduced: translate("stack-delivery-note-volume-produced"),
      map: translate("stack-delivery-note-map"),
      gps: translate("stack-delivery-note-gps"),
      nearestAddress: translate("nearest-address"),
      origin: translate("stack-delivery-note-origin"),
      link: {
        label: translate("stack-delivery-note-map-link"),
        href: `https://www.google.com/maps?q=${location.lat},${location.lng}`,
      },
    },
    overviewData: {
      stackNumber: inventoryDisplayReference,
      assortment: translate("inventory-item-WoodChipStack"),
      species: speciesArrText,
      depth: logLengthStr,
      volumeRm: result.stackedVolumeM3
        ? `${beautifyNumber(result.stackedVolumeM3, 2)} ${translate("unit-rm")}`
        : "-",
      volumeM3: result.volumeM3 ? `${beautifyNumber(result.volumeM3, 2)} m3` : "-",
      volumeProduced: manualChippedVolumeRM
        ? `${beautifyNumber(manualChippedVolumeRM, 2)} ${translate("unit-rm")}`
        : result.volumeM3
          ? `${beautifyNumber(
            result.volumeM3 * DEFAULT_VOLUME_TO_CHIPS_PILE_CONVERSION_FACTOR,
            2,
          )} ${translate("unit-rm")}`
          : "-",
      gpsCoordinates: { lat: location.lat, long: location.lng },
      nearestAddress: nearestAddress || undefined,
      origin: originsStr,
    },

    measurementsLabels: {
      header: translate("stack-delivery-note-measurements-and-observations"),
      section: translate("stack-delivery-note-section"),
      front: translate("stack-delivery-note-measurements"),
      correction: translate("stack-delivery-note-correction"),
      middleValues: translate("stack-delivery-note-middle-values"),
      height: translate("stack-delivery-note-stack-height"),
      length: translate("stack-delivery-note-stack-length"),
      solidPercentage: translate("stack-delivery-note-solid-percentage"),
    },
    measurementsData: {
      measurements: measArr,
      avgHeight: averageStackHeightMNum ? `${beautifyNumber(displayInventoryValue(averageStackHeightMNum, "heightM"), 2)} m` : "-",
      avgLength: stackLengthStr,
      solidPercentage: result.compensationFactorPercentage
        ? `${beautifyNumber(result.compensationFactorPercentage, 2)} %`
        : "-",
    },

    metricsLabels: {
      volumeAndWeight: translate("stack-delivery-note-volume-and-weight"),
      volumeRm: translate("stack-delivery-note-volume-rm"),
      volumeM3: translate("stack-delivery-note-volume-m3"),
      volumeProduced: translate("stack-delivery-note-volume-produced"),
      bark: false
        ? translate("stack-delivery-note-under-bark")
        : translate("stack-delivery-note-over-bark"),
    },
    metricsData: {
      volumeRm: result.stackedVolumeM3
        ? `${beautifyNumber(result.stackedVolumeM3, 2)} ${translate("unit-rm")}`
        : "-",
      volumeM3: result.volumeM3 ? `${beautifyNumber(result.volumeM3, 2)} m3` : "-",
      volumeProduced: manualChippedVolumeRM
        ? `${beautifyNumber(manualChippedVolumeRM, 2)} ${translate("unit-rm")}`
        : result.volumeM3
          ? `${beautifyNumber(
            result.volumeM3 * DEFAULT_VOLUME_TO_CHIPS_PILE_CONVERSION_FACTOR,
            2,
          )} ${translate("unit-rm")}`
          : "-",
    },

    stackDeliveryNoteFooter: translate(`print-footer-${iaVersion}`),
  };

  // --- STOKKE --- //
  const dataObjectLog = {
    headerLabels: {
      title: translate("delivery-note-title"),
      date: translate("stack-delivery-note-date"),
      buyer: translate("stack-delivery-note-buyer"),
      seller: translate("stack-delivery-note-seller"),
      measuredBy: translate("stack-delivery-note-measured-by"),
      forest: translate("stack-delivery-note-forest"),
      certificate: translate("stack-delivery-note-certificate"),
      claims: translate("stack-delivery-note-claims"),
      note: translate("stack-delivery-note-note"),
    },
    headerData: {
      date: `${dayjs().format("DD.MM.YYYY")}`,
      seller: seller,
      buyer: buyer,
      measuredBy: measResponsible,
      certificateNumber: deliveryNoteCertification.certificateNumbers,
      claims: deliveryNoteCertification.claims,
      note: inventoryStatusNote || "-",
      forest: forestName,
    },

    overviewLabels: {
      header: translate("stack-delivery-note-batch-data"),
      stackNumber: translate("stack-delivery-note-stack-number"),
      assortment: translate("stack-delivery-note-assortment"),
      species: translate("stack-delivery-note-species"),
      volumeM3: translate("stack-delivery-note-volume-m3"),
      map: translate("stack-delivery-note-map"),
      gps: translate("stack-delivery-note-gps"),
      nearestAddress: translate("nearest-address"),
      origin: translate("stack-delivery-note-origin"),
      link: {
        label: translate("stack-delivery-note-map-link"),
        href: `https://www.google.com/maps?q=${location.lat},${location.lng}`,
      },
      quality: translate("delivery-note-batch-class"),
      measMethod: translate("delivery-note-meas-method"),
      longestLog: translate("delivery-note-longest-log"),
      shortestLog: translate("delivery-note-shortest-log"),
      avgLength: translate("delivery-note-avg-length"),
      avgVol: translate("delivery-note-avg-vol"),
    },
    overviewData: {
      stackNumber: inventoryDisplayReference,
      assortment: assortmentStr,
      species: speciesArrText,
      volumeM3: totalLogVolume ? `${beautifyNumber(totalLogVolume, 2)} m3` : "-",
      gpsCoordinates: { lat: location.lat, long: location.lng },
      nearestAddress: nearestAddress || undefined,
      origin: originsStr,
      quality:
        classifications.length > 0
          ? classifications.sort((a, b) => a.localeCompare(b)).join(", ")
          : "-",
      measMethod: translate("delivery-note-hand-meas"),
      longestLog: longestLogLength ? `${beautifyNumber(longestLogLength, 2)} m` : "-",
      shortestLog: shortestLogLength ? `${beautifyNumber(shortestLogLength, 2)} m` : "-",
      avgLength: averageLogLengthM ? `${beautifyNumber(averageLogLengthM, 2)} m` : "-",
      avgVol: averageLogVolume ? `${beautifyNumber(averageLogVolume, 2)} m3` : "-",
    },

    summeryHeader: translate("delivery-note-summery-header"),
    summaryTableHeader: [
      translate("delivery-note-summary-d-class"),
      "13 - 15,9 cm",
      "16 - 20,9 cm",
      "21 - 25,9 cm",
      "26 - 30,9 cm",
      translate("delivery-note-summary-above") + " 31 cm",
      translate("delivery-note-summary-total"),
    ],
    summaryRows: [
      [
        translate("delivery-note-numbers-of-logs"),
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalLogs[0] : 0, 0)}`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalLogs[1] : 0, 0)}`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalLogs[2] : 0, 0)}`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalLogs[3] : 0, 0)}`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalLogs[4] : 0, 0)}`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalLogsCount : 0, 0)}`,
      ],
      [
        translate("delivery-note-summary-vol"),
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalVol[0] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalVol[1] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalVol[2] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalVol[3] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalVol[4] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalVolSum : 0, 2)} m3`,
      ],
      [
        translate("delivery-note-avg-length"),
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgLength[0] : 0, 2)} m`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgLength[1] : 0, 2)} m`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgLength[2] : 0, 2)} m`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgLength[3] : 0, 2)} m`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgLength[4] : 0, 2)} m`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalAvgLength : 0, 2)} m`,
      ],
      [
        translate("delivery-note-avg-vol-log"),
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgVolPerLog[0] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgVolPerLog[1] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgVolPerLog[2] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgVolPerLog[3] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.avgVolPerLog[4] : 0, 2)} m3`,
        `${beautifyNumber(logsSummaryData ? logsSummaryData.totalAvgVolPerLog : 0, 2)} m3`,
      ],
    ] as [string, ...string[]][],

    detailsHeader: translate("delivery-note-details-header"),
    detailsTableHeader: [
      translate("delivery-note-details-id"),
      translate("delivery-note-details-species"),
      translate("delivery-note-details-length"),
      translate("delivery-note-details-diameter"),
      translate("delivery-note-details-vol"),
      translate("delivery-note-details-class"),
    ],
    detailsRows: rows as [string, ...string[]][],

    stackDeliveryNoteFooter: translate(`print-footer-${iaVersion}`),
  };

  // Set document for download
  let document = <WoodStackDeliveryNote printLogo={printLogoDataUrl} {...dataObjectStack} />;
  if (variant === "WoodChipStack") {
    document = <WoodChipStackDeliveryNote printLogo={printLogoDataUrl} {...dataObjectChipStack} />;
  } else if (variant === "WoodLogStack") {
    document = <WoodLogDeliveryNote printLogo={printLogoDataUrl} {...dataObjectLog} />;
  }

  const countryCode = userOrg?.countryCode;
  const currency = getPrimaryCurrency(countryCode);

  // Determine whether to show contact selection or text input for handling companies based on configuration and approval status
  let displayLoggingCompanyContact = inventoryDataConfig.loggingCompanyType === "contact";
  let displayForwardingCompanyContact = inventoryDataConfig.forwardingCompanyType === "contact";
  let displayChippingCompanyContact = inventoryDataConfig.chippingCompanyType === "contact";
  if (disableInputs || buyerApprovedDisable) {
    displayLoggingCompanyContact = false;
    displayForwardingCompanyContact = false;
    displayChippingCompanyContact = false;
  }
  const buyerApprovedStatusChangeDisabled = inventoryDataConfig.buyerApproval !== "enabled";
  const confirmationRequirements = buyerApprovalRequirements.filter(
    (requirement) => requirement.type === "confirmation",
  );
  const accessibilityRequired = buyerApprovalRequirements.some(
    (requirement) => requirement.type === "accessibility",
  );
  const hasUnmetRequiredBuyerApprovalRequirement = buyerApprovalRequirements.some(
    (requirement) =>
      requirement.required &&
      (requirement.type === "accessibility"
        ? accessibility.length === 0
        : !requirement.requirementMet),
  );
  const releaseToBuyerDisabled =
    disableInputs ||
    buyerApprovedDisable ||
    selectedBuyer.length === 0 ||
    (!releasedToBuyer &&
      (!buyerApprovalRequirementsLoaded || hasUnmetRequiredBuyerApprovalRequirement));
  const showPricePerUnit = inventoryDataConfig.pricePerUnit !== "hidden";
  const showInventoryStatus = inventoryDataConfig.inventoryStatus !== "hidden";
  const showLoggingCompany = inventoryDataConfig.loggingCompanyType !== "hidden";
  const showForwardingCompany = inventoryDataConfig.forwardingCompanyType !== "hidden";
  const showChippingCompany = inventoryDataConfig.chippingCompanyType !== "hidden";
  const showHandlingCompanies =
    showLoggingCompany ||
    showForwardingCompany ||
    (variant === "WoodChipStack" && showChippingCompany);
  const showDeliveryNote = inventoryDataConfig.deliveryNoteSection !== "hidden";

  const handleBuyerApprovalRequirementChange = (id: string, requirementMet: boolean) => {
    setBuyerApprovalRequirements(
      buyerApprovalRequirements.map((requirement) => {
        if (requirement.id !== id || requirement.type !== "confirmation") return requirement;

        if (!requirementMet) {
          const { confirmedAt, confirmedBy, ...requirementWithoutConfirmation } = requirement;
          return { ...requirementWithoutConfirmation, requirementMet: false };
        }

        return {
          ...requirement,
          requirementMet: true,
          confirmedAt: dayjs().format(),
          confirmedBy: {
            id: user?.id || "",
            name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
          },
        };
      }),
    );

    // A release is no longer valid if its seller confirmation is withdrawn.
    if (!requirementMet && releasedToBuyer) setReleasedToBuyer(false);
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">{translate("raw-wood-input-handling-header")}</Typography>
      </Grid>

      <Grid item xs={12} sm={showPricePerUnit ? 9 : 12}>
        <Grid container rowSpacing={2} columnSpacing={1}>
          <Grid item xs={12} sm={showPricePerUnit ? 8 : 6}>
            <Stack direction={"row"} spacing={1} sx={{ minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AutoCompleteGrouped
                  title={translate("raw-wood-input-handling-buyer")}
                  label={translate("raw-wood-input-handling-buyer")}
                  showLabel={true}
                  showTitle={false}
                  groupByField="orgName"
                  renderOptionField="nameAndOrgName"
                  renderTagField="nameAndOrgName"
                  data={buyerOptions}
                  value={selectedBuyer}
                  changeHandler={handleChangeBuyer}
                  multiple={true}
                  noWrap={true}
                  disabled={disableInputs || buyerApprovedDisable || buyerSelectionLocked}
                />
              </Box>

              {showPricePerUnit && <Divider orientation="vertical" flexItem />}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={showPricePerUnit ? 4 : 6}>
            <FormControlLabel
              value={"test"}
              control={
                <Checkbox
                  checked={releasedToBuyer}
                  className="size-medium"
                  onChange={async () => {
                    const releaseAccepted = await setReleasedToBuyer(!releasedToBuyer);
                    if (!releasedToBuyer && releaseAccepted) {
                      await generateAndSaveDeliveryNote();
                    }
                  }}
                />
              }
              label={
                translate("raw-wood-input-handling-buyer-send-text-1") +
                ` ${title} ` +
                translate("raw-wood-input-handling-buyer-send-text-2")
              }
              labelPlacement="end"
              disabled={releaseToBuyerDisabled || generatingDeliveryNote}
              sx={{
                ml: 0,
                width: "100%",
                minWidth: 0,
                "& .MuiFormControlLabel-label": {
                  minWidth: 0,
                  overflowWrap: "anywhere",
                },
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      {showPricePerUnit && (
        <Grid item xs={12} sm={3}>
          <TextFieldInput
            title={translate("raw-wood-input-handling-price-per-unit")}
            value={price}
            handleChange={(e) => setPrice(e.target.value.replace(".", ","))}
            showLabel={true}
            fullWidth={true}
            disabled={
              disableInputs ||
              buyerApprovedDisable ||
              inventoryDataConfig.pricePerUnit !== "enabled"
            }
            inputAdornment={currency}
          />
        </Grid>
      )}

      {selectedBuyer.length > 0 &&
        (confirmationRequirements.length > 0 || accessibilityRequired) && (
          <>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {translate("raw-wood-input-handling-buyer-requirements")}
              </Typography>
            </Grid>

            {accessibilityRequired && (<>
              <Grid item xs={12} sm={3}>
                <Selector
                  title={translate("raw-wood-input-handling-accessibility")}
                  value={accessibility}
                  options={accessibilityOptions}
                  label={true}
                  fullWidth={true}
                  error={false}
                  multiple={true}
                  disabled={disableInputs || buyerApprovedDisable}
                  changeHandler={(event) => {
                    const value = event.target.value;
                    setAccessibility(Array.isArray(value) ? value : value.split(","));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={9}>
                <TextFieldInput
                  title={translate("raw-wood-input-handling-accessibility-note")}
                  value={accessibilityNote}
                  showLabel={true}
                  handleChange={(event) => setAccessibilityNote(event.target.value)}
                  fullWidth={true}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              </Grid>
            </>)}

            <Grid item xs={12}>
              {confirmationRequirements.map((requirement) => {
                return (
                  <FormControlLabel
                    key={requirement.id}
                    control={
                      <Checkbox
                        checked={requirement.requirementMet}
                        onChange={(event) =>
                          handleBuyerApprovalRequirementChange(
                            requirement.id,
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label={requirement.label}
                    disabled={disableInputs || buyerApprovedDisable}
                    sx={{ ml: 0, mt: -1 }}
                  />
                );
              })}
            </Grid>
          </>
        )}

      {showHandlingCompanies && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5">
              {translate("raw-wood-input-handling-parties-header")}
            </Typography>
          </Grid>

          {showLoggingCompany && (
            <Grid item xs={12} sm={12 / 2}>
              {!displayLoggingCompanyContact && (
                <TextFieldInput
                  title={translate("raw-wood-input-handling-logging-company")}
                  value={selectedLoggingCompanyString}
                  showLabel={true}
                  fullWidth={true}
                  placeholder={translate("raw-wood-input-handling-company-placeholder")}
                  handleChange={(e) => setSelectedLoggingCompanyString(e.target.value)}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              )}
              {displayLoggingCompanyContact && (
                <AutoCompleteGrouped
                  title={translate("raw-wood-input-handling-logging-company")}
                  label={translate("raw-wood-input-handling-logging-company")}
                  showLabel={true}
                  showTitle={false}
                  groupByField="orgName"
                  renderOptionField="nameAndOrgName"
                  renderTagField="nameAndOrgName"
                  data={companyOptions}
                  value={selectedLoggingCompany}
                  changeHandler={handleChangeLoggingCompany}
                  multiple={true}
                  noWrap={true}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              )}
            </Grid>
          )}

          {showForwardingCompany && (
            <Grid item xs={12} sm={12 / 2}>
              {!displayForwardingCompanyContact && (
                <TextFieldInput
                  title={translate("raw-wood-input-handling-forwarding-company")}
                  value={selectedForwardingCompanyString}
                  showLabel={true}
                  fullWidth={true}
                  placeholder={translate("raw-wood-input-handling-company-placeholder")}
                  handleChange={(e) => setSelectedForwardingCompanyString(e.target.value)}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              )}
              {displayForwardingCompanyContact && (
                <AutoCompleteGrouped
                  title={translate("raw-wood-input-handling-forwarding-company")}
                  label={translate("raw-wood-input-handling-forwarding-company")}
                  showLabel={true}
                  showTitle={false}
                  groupByField="orgName"
                  renderOptionField="nameAndOrgName"
                  renderTagField="nameAndOrgName"
                  data={companyOptions}
                  value={selectedForwardingCompany}
                  changeHandler={handleChangeForwardingCompany}
                  multiple={true}
                  noWrap={true}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              )}
            </Grid>
          )}

          {variant === "WoodChipStack" && showChippingCompany && (
            <Grid item xs={12} sm={12 / 2}>
              {!displayChippingCompanyContact && (
                <TextFieldInput
                  title={translate("raw-wood-input-handling-chipping-company")}
                  value={selectedChippingCompanyString}
                  showLabel={true}
                  fullWidth={true}
                  placeholder={translate("raw-wood-input-handling-company-placeholder")}
                  handleChange={(e) => setSelectedChippingCompanyString(e.target.value)}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              )}
              {displayChippingCompanyContact && (
                <AutoCompleteGrouped
                  title={translate("raw-wood-input-handling-chipping-company")}
                  label={translate("raw-wood-input-handling-chipping-company")}
                  showLabel={true}
                  showTitle={false}
                  groupByField="orgName"
                  renderOptionField="nameAndOrgName"
                  renderTagField="nameAndOrgName"
                  data={companyOptions}
                  value={selectedChippingCompany}
                  changeHandler={handleChangeChippingCompany}
                  multiple={true}
                  noWrap={true}
                  disabled={disableInputs || buyerApprovedDisable}
                />
              )}
            </Grid>
          )}
        </>
      )}

      <Grid item xs={12}>
        <Divider sx={{ mt: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h5">{translate("raw-wood-input-handling-status-header")}</Typography>
      </Grid>

      {showInventoryStatus && (
        <>
          <Grid item xs={12} sm={3}>
            <Selector
              title={translate("raw-wood-input-handling-status")}
              value={selectedStatus}
              options={statusOptions}
              label={true}
              fullWidth={true}
              error={false}
              disabled={disableInputs || buyerApprovedDisable}
              changeHandler={(e) => setSelectedStatus(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={9}>
            <TextFieldInput
              title={translate("raw-wood-input-handling-note")}
              value={inventoryStatusNote}
              showLabel={true}
              handleChange={(e) => setInventoryStatusNote(e.target.value)}
              fullWidth={true}
              disabled={disableInputs || buyerApprovedDisable}
            />
          </Grid>
        </>
      )}

      {inventoryDataConfig.buyerApproval !== "hidden" && (
        <>
          <Grid item xs={12} sm={3}>
            <Selector
              title={translate("raw-wood-input-handling-buyer-approval")}
              value={selectedBuyerApprovalStatus}
              options={buyerApprovalOptions}
              label={true}
              fullWidth={true}
              error={false}
              changeHandler={(e) =>
                setSelectedBuyerApprovalStatus(e.target.value as BuyerStatusChangeProps["status"])
              }
              disabled={
                buyerApprovedStatusChangeDisabled ||
                !releasedToBuyer
              }
            />
          </Grid>

          <Grid item xs={12} sm={9}>
            <TextFieldInput
              title={translate("raw-wood-input-handling-buyer-note")}
              value={buyerNote}
              handleChange={(e) => setBuyerNote(e.target.value)}
              fullWidth
              showLabel
              disabled={buyerApprovedStatusChangeDisabled}
            />
          </Grid>
        </>
      )}

      {showDeliveryNote && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Typography variant="h5">{translate("raw-wood-input-handling-del-note")}</Typography>

              <PDFDownloadLink
                document={document}
                fileName={`${translate("delivery-note-title")}-${inventoryDisplayReference}`}
              >
                {({ loading, blob }) => (
                  <Tooltip
                    title={translate("raw-wood-input-handling-delivery-note-download-tooltip")}
                  >
                    <span>
                      <IconButton
                        variant="light"
                        color="secondary"
                        sx={{ bgcolor: "grey.100" }}
                        disabled={loading}
                        onClick={() => handleDeleviryNoteClick(blob)}
                      >
                        <DownloadIcon fontSize="small" color={loading ? "disabled" : "primary"} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </PDFDownloadLink>
            </Stack>
          </Grid>
        </>
      )}
    </>
  );
};

export default RawWoodInputHandling;
